import { useEffect, useState } from 'react';

const RESEND_TIMER_INITIAL = 30;

export type AuthMode = 'mobile' | 'email';
export type AuthStep = 'input' | 'otp';

/**
 * Unified hook for login/signup auth form effects.
 * Handles verifyType, resets, resend timer, error sync, and optional progress.
 */
export function useAuthStepEffects({
  mode,
  setVerifyType,
  setStep,
  setOtpSent,
  setError,
  setOtpValue,
  setInputValue,
  setOtp,
  step,
  registerError,
  progress,
  setProgress,
  sendOtp,
  verifyOtp,
  inputValue,
  dialCode,
  otpValue,
  navigate,
  onOtpSuccess,
  isSignup,
}: {
  mode?: string;
  setVerifyType: (v: 'sms' | 'email') => void;
  setStep?: (v: 'input' | 'otp') => void;
  setOtpSent?: (v: boolean) => void;
  setError: (v: string | null) => void;
  setOtpValue?: (v: string) => void;
  setInputValue?: (v: string) => void;
  setOtp?: (v: string) => void;
  step?: 'input' | 'otp';
  registerError?: string | null;
  progress?: number;
  setProgress?: (v: number) => void;
  sendOtp?: (...args: any[]) => Promise<any>;
  verifyOtp?: (...args: any[]) => Promise<any>;
  inputValue?: string;
  dialCode?: string;
  otpValue?: string;
  navigate?: (path: string, opts?: any) => void;
  onOtpSuccess?: (params: any) => void;
  isSignup?: boolean;
}) {
  // Progress bar (for signup)
  const [progressValue, setProgressValue] = useState(progress ?? 0);
  // Resend timer
  const [resendTimer, setResendTimer] = useState(RESEND_TIMER_INITIAL);

  // Combine: Sync verifyType, reset states on mode change, and registerError sync
  // Only reset fields when mode changes
  useEffect(() => {
    if (mode === 'mobile') {
      setVerifyType('sms');
    } else {
      setVerifyType('email');
    }
    if (setStep) setStep('input');
    if (setOtpSent) setOtpSent(false);
    setError(null);
    if (setOtpValue) setOtpValue('');
    if (setInputValue) setInputValue('');
    if (setOtp) setOtp('');
    setResendTimer(RESEND_TIMER_INITIAL);
  }, [mode]);

  // Sync registerError to local error (for signup)
  useEffect(() => {
    if (registerError !== undefined) setError(registerError);
  }, [registerError, setError]);

  // Resend timer countdown
  useEffect(() => {
    if ((step === undefined || step === 'otp') && resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(t => t - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [step, resendTimer]);

  // Progress bar effect (for signup, optional)
  useEffect(() => {
    if (setProgress) {
      const timeout = setTimeout(() => setProgress(50), 100);
      return () => clearTimeout(timeout);
    } else {
      const timeout = setTimeout(() => setProgressValue(50), 100);
      return () => clearTimeout(timeout);
    }
  }, [setProgress]);

  // --- Handlers ---
  const handleResend = async () => {
    setError(null);
    if (setOtpValue) setOtpValue('');
    setResendTimer(RESEND_TIMER_INITIAL);
    try {
      if (sendOtp) {
        if (mode === 'mobile' && dialCode && inputValue) {
          await sendOtp({ dialCode, mobile: inputValue });
        } else if (inputValue) {
          await sendOtp({ email: inputValue });
        }
      }
    } catch (err: any) {
      let msg = err?.message || 'Failed to resend OTP';
      if (err?.response?.data?.message) {
        if (Array.isArray(err.response.data.message)) {
          msg = err.response.data.message.join(', ');
        } else {
          msg = err.response.data.message;
        }
      }
      setError(msg);
    }
  };

  const handleCta = async () => {
    setError(null);
    if (step === 'input') {
      try {
        let result;
        if (sendOtp) {
          if (mode === 'mobile' && dialCode && inputValue) {
            result = await sendOtp({ dialCode, mobile: inputValue });
          } else if (inputValue) {
            result = await sendOtp({ email: inputValue });
          }
        }
        if (result && result.success === true) {
          if (setStep) setStep('otp');
          if (setOtpSent) setOtpSent(true);
          setResendTimer(RESEND_TIMER_INITIAL);
        } else {
          setError(result?.message || 'Failed to send OTP.');
        }
      } catch (err: any) {
        setError(err?.response?.data?.message || err?.message || 'Failed to send OTP.');
      }
    } else if (step === 'otp') {
      try {
        let response;
        if (verifyOtp) {
          if (mode === 'mobile' && dialCode && inputValue) {
            response = await verifyOtp(otpValue, { dialCode, mobile: inputValue }, !isSignup);
          } else if (inputValue) {
            response = await verifyOtp(otpValue, { email: inputValue }, !isSignup);
          }
        }
        // For signup, call onOtpSuccess or navigate
        if (isSignup && response && response.success) {
          if (onOtpSuccess) {
            if (mode === 'mobile') {
              onOtpSuccess({ mobile: inputValue, dialCode });
            } else {
              onOtpSuccess({ email: inputValue });
            }
          } else if (navigate) {
            if (mode === 'mobile') {
              navigate('/register/signupconfirm', { state: { mobile: inputValue, dialCode } });
            } else {
              navigate('/register/signupconfirm', { state: { email: inputValue } });
            }
          }
        }
        // For login, verifyOtp in useAuth handles navigation via handleAuthSuccess
        // No need to do anything here - just let it complete
      } catch (err: any) {
        setError(err?.message || 'Invalid OTP');
      }
    }
  };

  return {
    resendTimer,
    setResendTimer,
    progressValue: setProgress ? progress ?? 0 : progressValue,
    setProgress: setProgress || setProgressValue,
    handleResend,
    handleCta,
  };
}
